import React, { useState } from 'react';
import { Calculator, Trash2, AlertCircle } from 'lucide-react';
import { ViolationSelector } from './ViolationSelector';
import { ScenarioViolationList } from './ScenarioViolationList';
import { ImpactPreview } from './ImpactPreview';
import { predictionsApi } from '../../api/predictions.api';
import type { HypotheticalViolation, WhatIfResponse, AllBasicPredictions } from '../../types/predictions';
import { logger } from '../../utils/logger';

interface ScenarioBuilderProps {
  dotNumber: number;
  currentPredictions: AllBasicPredictions;
}

/**
 * ScenarioBuilder Component
 *
 * Main container for what-if scenario analysis
 *
 * Features:
 * - Add multiple hypothetical violations
 * - Remove violations from scenario
 * - Calculate impact on BASIC scores
 * - Display before/after comparison
 * - Clear all violations
 * - Error handling and loading states
 *
 * Workflow:
 * 1. User adds violations via ViolationSelector
 * 2. Violations appear in ScenarioViolationList
 * 3. User clicks "Calculate Impact"
 * 4. ImpactPreview shows predicted changes
 */
export const ScenarioBuilder: React.FC<ScenarioBuilderProps> = ({
  dotNumber,
  currentPredictions,
}) => {
  const [violations, setViolations] = useState<HypotheticalViolation[]>([]);
  const [impactResult, setImpactResult] = useState<WhatIfResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle adding a new violation to the scenario
   */
  const handleAddViolation = (violation: HypotheticalViolation) => {
    setViolations([...violations, violation]);
    // Clear previous results when violations change
    setImpactResult(null);
    setError(null);
  };

  /**
   * Handle removing a violation from the scenario
   */
  const handleRemoveViolation = (index: number) => {
    const newViolations = violations.filter((_, i) => i !== index);
    setViolations(newViolations);
    // Clear results if no violations left
    if (newViolations.length === 0) {
      setImpactResult(null);
    }
    setError(null);
  };

  /**
   * Calculate impact of all violations
   */
  const handleCalculate = async () => {
    if (violations.length === 0) {
      setError('Please add at least one violation to calculate impact.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call what-if API
      const result = await predictionsApi.calculateWhatIf(dotNumber, violations);
      setImpactResult(result);
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Failed to calculate impact. Please check your violations and try again. The backend may need to be running.';
      logger.error('Error calculating what-if scenario:', { error: err, dotNumber, scenario: violations });
      setError(message);
      setImpactResult(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear all violations and results
   */
  const handleClear = () => {
    setViolations([]);
    setImpactResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-900">What-If Scenario Builder</h2>
        </div>
        <p className="text-gray-600">
          Add hypothetical violations to predict their impact on BASIC scores. This helps you
          understand risk exposure and plan prevention strategies.
        </p>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Add Violations */}
        <div className="space-y-6">
          {/* Violation Selector */}
          <ViolationSelector onAdd={handleAddViolation} />

          {/* Violation List */}
          <ScenarioViolationList violations={violations} onRemove={handleRemoveViolation} />

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCalculate}
              disabled={violations.length === 0 || loading}
              className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700
                       disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors
                       font-semibold flex items-center justify-center gap-2 shadow-md
                       hover:shadow-lg disabled:shadow-none"
            >
              <Calculator className="w-5 h-5" />
              {loading ? 'Calculating...' : 'Calculate Impact'}
            </button>

            <button
              onClick={handleClear}
              disabled={violations.length === 0 && !impactResult}
              className="px-6 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300
                       disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
                       transition-colors font-semibold flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-1">Error</h4>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Impact Preview */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          {loading ? (
            // Loading State
            <div className="bg-white rounded-lg shadow-md p-12 text-center border border-gray-200">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Calculating impact...</p>
              <p className="text-gray-400 text-sm mt-2">
                Analyzing {violations.length} violation{violations.length !== 1 ? 's' : ''}
              </p>
            </div>
          ) : impactResult ? (
            // Results Display
            <ImpactPreview baseline={currentPredictions} impactResult={impactResult} />
          ) : (
            // Empty State
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-12 text-center border-2 border-dashed border-gray-300 min-h-[400px] flex flex-col items-center justify-center">
              <Calculator className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Analysis Yet</h3>
              <p className="text-sm text-gray-500 max-w-md">
                Add one or more hypothetical violations on the left, then click "Calculate Impact"
                to see how they would affect BASIC scores.
              </p>

              {/* Quick Tips */}
              <div className="mt-6 bg-white rounded-lg p-4 text-left border border-gray-200 max-w-md">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Quick Tips:
                </p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Add multiple violations to see combined impact</li>
                  <li>• Toggle OOS to see 3x severity multiplier effect</li>
                  <li>• Recent violations have higher time-weighted impact</li>
                  <li>• Watch for threshold breach warnings</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">How It Works:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <p className="font-semibold mb-1">1. Add Violations</p>
            <p>
              Select CFR violations from the dropdown. Choose violations that could realistically
              occur in your operations.
            </p>
          </div>
          <div>
            <p className="font-semibold mb-1">2. Set Parameters</p>
            <p>
              Specify the violation date and check OOS if applicable. OOS violations have 3x the
              impact.
            </p>
          </div>
          <div>
            <p className="font-semibold mb-1">3. Calculate Impact</p>
            <p>
              Click "Calculate Impact" to see how these violations would affect your BASIC scores
              and percentiles.
            </p>
          </div>
          <div>
            <p className="font-semibold mb-1">4. Analyze Results</p>
            <p>
              Review the before/after comparison, watch for threshold breaches, and use insights
              for prevention planning.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioBuilder;
